/**
 * Badge components with Panerai dark theme
 * Status indicators with luminova-style glow effects
 */

/**
 * Base badge component
 */
export function Badge({
  children,
  variant = 'default',
  size = 'md',
  dot = false,
  removable = false,
  onRemove,
  className = '',
}) {
  const variantClasses = {
    default: 'bg-dial-elevated text-indices-medium border border-dial-border',
    primary: 'bg-lume-teal/20 text-lume-teal border border-lume-teal/30',
    success: 'bg-status-success/20 text-status-success border border-status-success/30',
    warning: 'bg-status-warning/20 text-status-warning border border-status-warning/30',
    error: 'bg-status-error/20 text-status-error border border-status-error/30',
    info: 'bg-status-info/20 text-status-info border border-status-info/30',
    purple: 'bg-purple-500/20 text-purple-400 border border-purple-500/30',
    teal: 'bg-teal-500/20 text-teal-400 border border-teal-500/30',
    orange: 'bg-orange-500/20 text-orange-400 border border-orange-500/30',
    cream: 'bg-lume-cream/20 text-lume-cream border border-lume-cream/30',
  }

  const sizeClasses = {
    sm: 'px-1.5 py-0.5 text-[10px]',
    md: 'px-2 py-0.5 text-xs',
    lg: 'px-2.5 py-1 text-sm',
  }

  const dotColors = {
    default: 'bg-indices-muted',
    primary: 'bg-lume-teal',
    success: 'bg-status-success',
    warning: 'bg-status-warning',
    error: 'bg-status-error',
    info: 'bg-status-info',
    purple: 'bg-purple-400',
    teal: 'bg-teal-400',
    orange: 'bg-orange-400',
    cream: 'bg-lume-cream',
  }

  return (
    <span
      className={`
        inline-flex items-center gap-1.5 rounded-full font-medium
        ${variantClasses[variant]}
        ${sizeClasses[size]}
        ${className}
      `}
    >
      {dot && (
        <span
          className={`w-1.5 h-1.5 rounded-full ${dotColors[variant]}`}
          aria-hidden="true"
        />
      )}
      {children}
      {removable && onRemove && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
          className="ml-0.5 hover:bg-white/10 rounded-full p-0.5 -mr-1 transition-colors"
          aria-label="Remove"
        >
          <svg
            className="w-3 h-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      )}
    </span>
  )
}

/**
 * Status badge with predefined status mappings
 */
export function StatusBadge({ status, className = '' }) {
  const statusConfig = {
    // General statuses
    active: { label: 'Active', variant: 'success' },
    inactive: { label: 'Inactive', variant: 'default' },
    pending: { label: 'Pending', variant: 'warning' },
    completed: { label: 'Completed', variant: 'success' },
    cancelled: { label: 'Cancelled', variant: 'error' },

    // Session statuses
    scheduled: { label: 'Scheduled', variant: 'info' },
    confirmed: { label: 'Confirmed', variant: 'success' },
    no_show: { label: 'No Show', variant: 'warning' },

    // Training types
    in_person: { label: 'In Person', variant: 'info' },
    online: { label: 'Online', variant: 'purple' },
    hybrid: { label: 'Hybrid', variant: 'teal' },

    // Memory types
    medical: { label: 'Medical', variant: 'error' },
    preference: { label: 'Preference', variant: 'purple' },
    personal: { label: 'Personal', variant: 'info' },
    goal: { label: 'Goal', variant: 'success' },
    behavioral: { label: 'Behavioral', variant: 'orange' },
    situational: { label: 'Situational', variant: 'default' },
  }

  const config = statusConfig[status] || {
    label: status?.replace(/_/g, ' ') || 'Unknown',
    variant: 'default',
  }

  return (
    <Badge variant={config.variant} className={className}>
      {config.label}
    </Badge>
  )
}

/**
 * Count badge (for notification counts, etc.)
 */
export function CountBadge({
  count,
  max = 99,
  variant = 'error',
  className = '',
}) {
  if (!count || count <= 0) return null

  const displayCount = count > max ? `${max}+` : count

  const variantClasses = {
    error: 'bg-status-error text-dial-black',
    primary: 'bg-lume-teal text-dial-black',
    default: 'bg-indices-muted text-dial-black',
  }

  return (
    <span
      className={`
        inline-flex items-center justify-center min-w-[18px] h-[18px] px-1
        text-[10px] font-bold rounded-full
        ${variantClasses[variant]}
        ${className}
      `}
    >
      {displayCount}
    </span>
  )
}

/**
 * Dot indicator (online/offline status, etc.)
 */
export function DotIndicator({
  status = 'default',
  size = 'md',
  pulse = false,
  className = '',
}) {
  const statusColors = {
    default: 'bg-indices-muted',
    online: 'bg-status-success',
    offline: 'bg-indices-muted',
    busy: 'bg-status-error',
    away: 'bg-status-warning',
    success: 'bg-status-success',
    error: 'bg-status-error',
    warning: 'bg-status-warning',
  }

  const sizeClasses = {
    sm: 'w-2 h-2',
    md: 'w-2.5 h-2.5',
    lg: 'w-3 h-3',
  }

  return (
    <span className={`relative flex ${className}`}>
      <span
        className={`
          rounded-full ${statusColors[status]} ${sizeClasses[size]}
          ${pulse ? 'animate-pulse-soft' : ''}
        `}
      />
      {pulse && (
        <span
          className={`
            absolute rounded-full ${statusColors[status]} ${sizeClasses[size]}
            animate-ping opacity-75
          `}
        />
      )}
    </span>
  )
}

/**
 * Tag badge (for categories, labels)
 */
export function Tag({
  children,
  color = 'gray',
  onClick,
  selected = false,
  className = '',
}) {
  const colorClasses = {
    gray: selected
      ? 'bg-indices-medium text-dial-black'
      : 'bg-dial-elevated text-indices-medium border border-dial-border hover:bg-dial-surface',
    blue: selected
      ? 'bg-status-info text-dial-black'
      : 'bg-status-info/20 text-status-info border border-status-info/30 hover:bg-status-info/30',
    green: selected
      ? 'bg-status-success text-dial-black'
      : 'bg-status-success/20 text-status-success border border-status-success/30 hover:bg-status-success/30',
    red: selected
      ? 'bg-status-error text-dial-black'
      : 'bg-status-error/20 text-status-error border border-status-error/30 hover:bg-status-error/30',
    purple: selected
      ? 'bg-purple-500 text-dial-black'
      : 'bg-purple-500/20 text-purple-400 border border-purple-500/30 hover:bg-purple-500/30',
    primary: selected
      ? 'bg-lume-teal text-dial-black'
      : 'bg-lume-teal/20 text-lume-teal border border-lume-teal/30 hover:bg-lume-teal/30',
  }

  const Component = onClick ? 'button' : 'span'

  return (
    <Component
      onClick={onClick}
      className={`
        inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium
        transition-colors
        ${colorClasses[color]}
        ${onClick ? 'cursor-pointer' : ''}
        ${className}
      `}
    >
      {children}
    </Component>
  )
}

/**
 * Progress rating badge (for change assessments)
 */
export function ProgressBadge({ rating, className = '' }) {
  const ratingConfig = {
    significant_positive: { label: 'Great Progress', variant: 'success' },
    moderate: { label: 'Good Progress', variant: 'info' },
    minimal: { label: 'Steady', variant: 'default' },
    concerning: { label: 'Needs Attention', variant: 'warning' },
  }

  const config = ratingConfig[rating] || { label: rating || 'Unknown', variant: 'default' }

  return (
    <Badge variant={config.variant} className={className}>
      {config.label}
    </Badge>
  )
}

export default {
  Badge,
  StatusBadge,
  CountBadge,
  DotIndicator,
  Tag,
  ProgressBadge,
}
